import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'

interface UnsavedChangesContextValue {
  /** A surface says whether it is holding work the user has not saved. */
  report: (key: string, unsaved: boolean) => void
  /** Do this, or ask first if anything on screen would lose work by it. */
  guard: (action: () => void) => void
}

/**
 * Nothing registered and nothing to ask: the default lets every action through,
 * so a tree without the provider behaves as it did before there was one.
 */
const FALLBACK: UnsavedChangesContextValue = {
  report: () => undefined,
  guard: (action) => action(),
}

const UnsavedChangesContext =
  createContext<UnsavedChangesContextValue>(FALLBACK)

interface UnsavedChangesProviderProps {
  children: React.ReactNode
}

/**
 * One place to ask "is anything on screen unsaved?", for the controls that
 * would throw it away.
 *
 * The two halves of the question are far apart in the tree: a document knows it
 * has been edited, and the button that closes it is up in a panel header three
 * or four components above. Rather than thread a flag through every surface
 * that can show a file, whatever holds unsaved work says so here, and whatever
 * closes it asks here.
 */
export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({
  children,
}) => {
  // Held as a ref as well as state: guard() reads it at the moment of the
  // click, which is not a render.
  const unsavedRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState<{ run: () => void } | null>(null)

  const report = useCallback((key: string, unsaved: boolean) => {
    if (unsaved) {
      unsavedRef.current.add(key)
    } else {
      unsavedRef.current.delete(key)
    }
  }, [])

  const guard = useCallback((action: () => void) => {
    if (unsavedRef.current.size === 0) {
      action()
      return
    }
    setPending({ run: action })
  }, [])

  const value = useMemo(() => ({ report, guard }), [report, guard])

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null)
          }
        }}
      >
        <AlertDialogContent className="sm:max-w-[412px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have comments and edits that have not been saved to this file.
              Closing it now will lose them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="outline-compact">
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              variant="danger-compact"
              onClick={() => {
                const action = pending?.run
                setPending(null)
                action?.()
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  )
}

/**
 * Say, for as long as this stays mounted, whether it is holding unsaved work.
 * The key is the thing being held — one file, one key — so two open documents
 * do not answer for each other.
 */
export const useReportUnsavedChanges = (
  key: string,
  unsaved: boolean,
): void => {
  const { report } = useContext(UnsavedChangesContext)
  useEffect(() => {
    report(key, unsaved)
    return () => report(key, false)
  }, [key, unsaved, report])
}

/**
 * Wrap something that would throw unsaved work away — closing a file, a tab, a
 * panel. It runs straight away when there is nothing to lose.
 */
export const useUnsavedChangesGuard = (): ((action: () => void) => void) =>
  useContext(UnsavedChangesContext).guard
